import React from 'react';
import { ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react';

// ---------------------------------------------------------------------------
// Formatters — used across all three tabs.
// ---------------------------------------------------------------------------

export const formatMoney = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '$0.00';
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatMoneyCompact = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
};

export const formatPercent = (val, digits = 1) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return '0%';
  return `${n.toFixed(digits)}%`;
};

export const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

// Renders the API's `YYYY-MM` value as the DS-canonical `MMM-YYYY` —
// e.g. `2026-05` → `May-2026`. The hyphen separator (not a space) is the
// portal-wide convention for compact month-year tokens shown in
// section headers, KPI subtitles, and source-of-truth banners.
export const formatMonthLabel = (label) => {
  if (!label) return '';
  const match = /^(\d{4})-(\d{2})$/.exec(label);
  if (!match) return label;
  const [, year, month] = match;
  const date = new Date(Number(year), Number(month) - 1, 1);
  const monthShort = date.toLocaleString('en-GB', { month: 'short' });
  return `${monthShort}-${year}`;
};

export const occupancyBarColor = (pct) => {
  const n = Number(pct) || 0;
  if (n >= 90) return 'bg-red-500';
  if (n >= 75) return 'bg-amber-500';
  if (n >= 50) return 'bg-emerald-500';
  return 'bg-gray-400';
};

/**
 * Normalise occupancy fields from the overview API.
 *
 * Denominator is **leased** inventory only (`leased_beds`). Pre-v1.3
 * payloads omit that field → fall back to `total_beds` so Overview still
 * renders until the portal ships leased counts.
 */
export const resolveOccupancy = (source = {}) => {
  const occupied = Number(source.occupied_beds_current) || 0;
  const leasedRaw = source.leased_beds;
  const fallback = Number(source.total_beds) || 0;
  const leased =
    leasedRaw === null || leasedRaw === undefined || leasedRaw === ''
      ? fallback
      : Math.max(0, Number(leasedRaw) || 0);
  const available =
    source.available_beds_current != null
      ? Math.max(0, Number(source.available_beds_current) || 0)
      : Math.max(0, leased - occupied);
  const pct =
    source.occupancy_rate_percent != null
      ? Number(source.occupancy_rate_percent) || 0
      : leased > 0
        ? Math.round((Math.min(occupied, leased) / leased) * 1000) / 10
        : 0;
  return {
    occupied,
    leased,
    available,
    pct,
  };
};

/** Occupancy label: "24 / 24 beds". */
export const formatLeasedOccupancy = (occ) => {
  const { occupied, leased } = resolveOccupancy(occ);
  return `${occupied} / ${leased} beds`;
};

/** KPI subtitle: "24 of 24 beds occupied". */
export const formatOccupancyContext = (occ) => {
  const { occupied, leased } = resolveOccupancy(occ);
  return leased > 0
    ? `${occupied} of ${leased} beds occupied`
    : 'No bed inventory';
};

// ---------------------------------------------------------------------------
// Banners — shared between tabs so the "live from external system" framing
// stays consistent across the page.
// ---------------------------------------------------------------------------

/**
 * Source-of-truth banner: makes it explicit to HR users that this is a
 * read-only projection of the Dormitory Portal, with a clear path to the
 * portal for any mutation. Sits above tab content.
 */
export const SourceOfTruthBanner = ({
  monthLabel,
  dormPortalUrl,
  onRefresh,
  loading,
}) => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 flex items-center sm:items-start justify-between gap-3 sm:gap-4">
    <div className="flex items-center sm:items-start gap-2 sm:gap-3 min-w-0">
      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 sm:mt-0.5" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-blue-900 truncate">
          Live from Dormitory Portal{monthLabel ? ` · ${formatMonthLabel(monthLabel)}` : ''}
        </p>
        {/* The explanation is helpful context but verbose — it dominates the
            banner on a phone. Hide it below sm; the title + "Open Portal"
            already convey the read-only, portal-owned model. */}
        <p className="hidden sm:block text-xs text-blue-700 mt-0.5">
          The Dormitory Portal owns dormitory locations, rent, occupants, and
          movements. To make changes, open the Dormitory Portal directly.
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="icon-button-admin"
          title="Refresh from Dormitory Portal (bypass cache)"
          aria-label="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      )}
      {dormPortalUrl && (
        <a
          href={dormPortalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-admin-secondary btn-sm"
          title="Open Dormitory Portal in a new tab"
        >
          <ExternalLink className="w-4 h-4" />
          <span className="hidden sm:inline">Open Portal</span>
        </a>
      )}
    </div>
  </div>
);

/**
 * Surfaces the case where the Dorm Portal is unreachable / not configured /
 * returning a 403. Layout below stays stable; this banner explains why the
 * numbers are zero.
 */
export const PortalUnavailableBanner = ({ error }) => (
  <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
    <div className="flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-amber-800">
          Dormitory Portal is currently unavailable
        </p>
        <p className="text-xs text-amber-700 mt-1">
          {error || 'The HR Portal could not reach the Dormitory Portal API.'}
        </p>
      </div>
    </div>
  </div>
);
